/**
 * usePayrollLoading - 급여 관리 로딩 상태 관리 Hook
 *
 * payroll-management.tsx의 로딩/실행 관련 상태를 분리
 * Phase 2 Step 3: 로딩 상태 Hook 분리
 *
 * @description
 * - 데이터 로딩 상태
 * - 저장 진행 상태
 * - 동시 실행 방지 플래그
 * - 팀별 실행 추적
 */

import { useState, useCallback } from 'react';

// Loading State Types
export interface PayrollLoadingState {
  /** 실제매출 데이터 로딩 중 */
  isLoadingActualSales: boolean;
  /** 이전 월 데이터 로딩 중 */
  isLoadingPreviousMonthData: boolean;
  /** 저장 진행 중 */
  isSaving: boolean;
  /** 일괄 조정 실행 중 */
  isBulkAdjustmentRunning: boolean;
  /** 전역 계산 실행 중 */
  isGlobalCalculationRunning: boolean;
  /** 현재 실행 중인 팀 Set */
  executingTeams: Set<string>;
}

export interface PayrollLoadingActions {
  // 실제매출 로딩
  setIsLoadingActualSales: (loading: boolean) => void;
  startLoadingActualSales: () => void;
  stopLoadingActualSales: () => void;

  // 이전 월 데이터 로딩
  setIsLoadingPreviousMonthData: (loading: boolean) => void;
  startLoadingPreviousMonthData: () => void;
  stopLoadingPreviousMonthData: () => void;

  // 저장 상태
  setIsSaving: (saving: boolean) => void;
  startSaving: () => void;
  stopSaving: () => void;

  // 일괄 조정
  setIsBulkAdjustmentRunning: (running: boolean) => void;
  startBulkAdjustment: () => boolean; // returns false if already running
  stopBulkAdjustment: () => void;

  // 전역 계산
  setIsGlobalCalculationRunning: (running: boolean) => void;
  startGlobalCalculation: () => boolean; // returns false if already running
  stopGlobalCalculation: () => void;

  // 팀별 실행 추적
  setExecutingTeams: React.Dispatch<React.SetStateAction<Set<string>>>;
  addExecutingTeam: (teamName: string) => boolean; // returns false if already executing
  removeExecutingTeam: (teamName: string) => void;
  isTeamExecuting: (teamName: string) => boolean;
  clearExecutingTeams: () => void;

  // 유틸리티
  isAnyOperationRunning: () => boolean;
  canStartNewOperation: () => boolean;
}

export type UsePayrollLoadingReturn = PayrollLoadingState & PayrollLoadingActions;

/**
 * 급여 관리 로딩 상태 Hook
 */
export function usePayrollLoading(): UsePayrollLoadingReturn {
  // === State Declarations ===

  // 실제매출 데이터 로딩 상태
  const [isLoadingActualSales, setIsLoadingActualSales] = useState<boolean>(false);

  // 이전 월 데이터 로딩 상태
  const [isLoadingPreviousMonthData, setIsLoadingPreviousMonthData] = useState<boolean>(false);

  // 저장 진행 상태
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // 일괄 조정 실행 상태
  const [isBulkAdjustmentRunning, setIsBulkAdjustmentRunning] = useState<boolean>(false);

  // 전역 계산 실행 상태
  const [isGlobalCalculationRunning, setIsGlobalCalculationRunning] = useState<boolean>(false);

  // 팀별 실행 추적
  const [executingTeams, setExecutingTeams] = useState<Set<string>>(new Set());

  // === Action Implementations ===

  // 실제매출 로딩 시작/종료
  const startLoadingActualSales = useCallback(() => {
    setIsLoadingActualSales(true);
  }, []);

  const stopLoadingActualSales = useCallback(() => {
    setIsLoadingActualSales(false);
  }, []);

  // 이전 월 데이터 로딩 시작/종료
  const startLoadingPreviousMonthData = useCallback(() => {
    setIsLoadingPreviousMonthData(true);
  }, []);

  const stopLoadingPreviousMonthData = useCallback(() => {
    setIsLoadingPreviousMonthData(false);
  }, []);

  // 저장 시작/종료
  const startSaving = useCallback(() => {
    setIsSaving(true);
  }, []);

  const stopSaving = useCallback(() => {
    setIsSaving(false);
  }, []);

  // 일괄 조정 시작 (동시 실행 방지)
  const startBulkAdjustment = useCallback((): boolean => {
    if (isBulkAdjustmentRunning) {
      return false;
    }
    setIsBulkAdjustmentRunning(true);
    return true;
  }, [isBulkAdjustmentRunning]);

  const stopBulkAdjustment = useCallback(() => {
    setIsBulkAdjustmentRunning(false);
  }, []);

  // 전역 계산 시작 (동시 실행 방지)
  const startGlobalCalculation = useCallback((): boolean => {
    if (isGlobalCalculationRunning) {
      return false;
    }
    setIsGlobalCalculationRunning(true);
    return true;
  }, [isGlobalCalculationRunning]);

  const stopGlobalCalculation = useCallback(() => {
    setIsGlobalCalculationRunning(false);
  }, []);

  // 팀 실행 추가 (상호 배제)
  const addExecutingTeam = useCallback((teamName: string): boolean => {
    if (executingTeams.has(teamName)) {
      return false;
    }
    setExecutingTeams(prev => new Set([...prev, teamName]));
    return true;
  }, [executingTeams]);

  // 팀 실행 제거
  const removeExecutingTeam = useCallback((teamName: string) => {
    setExecutingTeams(prev => {
      const newSet = new Set(prev);
      newSet.delete(teamName);
      return newSet;
    });
  }, []);

  // 팀 실행 중 여부 확인
  const isTeamExecuting = useCallback((teamName: string): boolean => {
    return executingTeams.has(teamName);
  }, [executingTeams]);

  // 실행 중인 팀 전체 초기화
  const clearExecutingTeams = useCallback(() => {
    setExecutingTeams(new Set());
  }, []);

  // 어떤 작업이라도 실행 중인지 확인
  const isAnyOperationRunning = useCallback((): boolean => {
    return (
      isLoadingActualSales ||
      isLoadingPreviousMonthData ||
      isSaving ||
      isBulkAdjustmentRunning ||
      isGlobalCalculationRunning ||
      executingTeams.size > 0
    );
  }, [
    isLoadingActualSales,
    isLoadingPreviousMonthData,
    isSaving,
    isBulkAdjustmentRunning,
    isGlobalCalculationRunning,
    executingTeams.size
  ]);

  // 새 작업 시작 가능 여부
  const canStartNewOperation = useCallback((): boolean => {
    return !isBulkAdjustmentRunning && !isGlobalCalculationRunning;
  }, [isBulkAdjustmentRunning, isGlobalCalculationRunning]);

  // === Return ===
  return {
    // State
    isLoadingActualSales,
    isLoadingPreviousMonthData,
    isSaving,
    isBulkAdjustmentRunning,
    isGlobalCalculationRunning,
    executingTeams,

    // Actions - 실제매출 로딩
    setIsLoadingActualSales,
    startLoadingActualSales,
    stopLoadingActualSales,

    // Actions - 이전 월 로딩
    setIsLoadingPreviousMonthData,
    startLoadingPreviousMonthData,
    stopLoadingPreviousMonthData,

    // Actions - 저장
    setIsSaving,
    startSaving,
    stopSaving,

    // Actions - 일괄 조정
    setIsBulkAdjustmentRunning,
    startBulkAdjustment,
    stopBulkAdjustment,

    // Actions - 전역 계산
    setIsGlobalCalculationRunning,
    startGlobalCalculation,
    stopGlobalCalculation,

    // Actions - 팀별 실행
    setExecutingTeams,
    addExecutingTeam,
    removeExecutingTeam,
    isTeamExecuting,
    clearExecutingTeams,

    // Utilities
    isAnyOperationRunning,
    canStartNewOperation,
  };
}

export default usePayrollLoading;
